import React, { useState, useEffect } from 'react';
import { Star, Quote, ArrowRight, CheckCircle } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

// Static testimonials always displayed at the top of the page
const STATIC_TESTIMONIALS = [
  {
    id: 'static-1',
    rating: 5,
    comment:
      'Mr Smith took amazing care of me. I felt very safe and protected. No need to look any further for your transportation needs. His rates are fair and reasonable. His communication and professionalism is unmatched. Thank you again Mr Smith!',
    attribution: 'Verified Customer (Tripadvisor)',
    verifiedSource: 'Tripadvisor',
    reviewUrl:
      'https://www.tripadvisor.in/ShowUserReviews-g147416-d34296506-r1056994579-Taxi_98-Nassau_New_Providence_Island_Bahamas.html',
  },
  {
    id: 'static-2',
    rating: 5,
    comment:
      'Steven was absolutely exceptional! Truly the best driver in the Bahamas. He made my entire stay effortless and enjoyable.',
    attribution: 'Roam14619654541 (Tripadvisor)',
    verifiedSource: 'Tripadvisor',
    reviewUrl:
      'https://www.tripadvisor.in/ShowUserReviews-g147416-d34296506-r1057921985-Taxi_98-Nassau_New_Providence_Island_Bahamas.html',
  },
  {
    id: 'static-3',
    rating: 5,
    comment:
      "Steve gives amazing service and doesn't over charge you. He made sure I was ok when I was sick during my vacation and told me to call him anytime I needed him to take me anywhere. He went above and beyond to make sure I was ok. And help me salvage my vacation. You can't go wrong with him as your driver. If you need a taxi or an island tour he's the guy to get you there.",
    attribution: 'sharon t (Tripadvisor)',
    verifiedSource: 'Tripadvisor',
    reviewUrl:
      'https://www.tripadvisor.in/ShowUserReviews-g147416-d34296506-r1057997405-Taxi_98-Nassau_New_Providence_Island_Bahamas.html',
  },
];

const PublicReviews = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchApprovedReviews();
  }, []);

  const fetchApprovedReviews = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || ''}/api/reviews`);
      const data = await response.json();
      setReviews(data);
    } catch (error) {
      console.error('Error fetching reviews:', error);
    } finally {
      setLoading(false);
    }
  };

  const renderStars = (rating) => {
    return [...Array(5)].map((_, index) => (
      <Star
        key={index}
        className={`w-5 h-5 ${
          index < rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'
        }`}
      />
    ));
  };

  const formatCustomerName = (name) => {
    if (!name) return 'Anonymous';
    const parts = name.split(' ');
    if (parts.length === 1) return parts[0];
    return `${parts[0]} ${parts[1]?.charAt(0)}.`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  return (
    <>
      <Helmet>
        <title>Customer Reviews | Taxi-98 Nassau Bahamas</title>
        <meta name="description" content="Read what our customers say about Taxi-98 airport taxi and transportation services in Nassau, Bahamas. Real reviews from real customers." />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-cyan-50 via-white to-blue-50 pt-24 pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Customer Reviews
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              See what our customers say about their experience with Taxi-98
            </p>
          </div>

          {/* Static Testimonials Intro */}
          <p
            data-testid="reviews-intro-copy"
            className="text-center text-lg text-gray-700 max-w-3xl mx-auto -mt-8 mb-12"
          >
            We're building our reputation one ride at a time. Here's what our customers are already saying:
          </p>

          {/* Static Testimonials Grid */}
          <div
            data-testid="reviews-static-testimonials-grid"
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16"
          >
            {STATIC_TESTIMONIALS.map((review) => (
              <div
                key={review.id}
                data-testid={`reviews-static-card-${review.id}`}
                className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow duration-300 flex flex-col"
              >
                <Quote className="w-8 h-8 text-cyan-500 mb-4" />
                <div
                  className="flex items-center mb-4"
                  aria-label={`${review.rating} out of 5 stars`}
                >
                  {renderStars(review.rating)}
                </div>
                <p className="text-gray-700 leading-relaxed">
                  "{review.comment}"
                </p>

                {review.verifiedSource && (
                  <div className="mt-5 pt-4 border-t border-gray-100 flex flex-col gap-2">
                    {review.attribution && (
                      <p className="text-sm font-semibold text-gray-900">
                        — {review.attribution}
                      </p>
                    )}
                    <div className="flex flex-wrap items-center gap-2">
                      <span
                        data-testid={`reviews-static-verified-badge-${review.id}`}
                        className="inline-flex items-center text-xs font-medium text-green-700 bg-green-50 border border-green-200 rounded-full px-2.5 py-1"
                      >
                        <CheckCircle className="w-3.5 h-3.5 mr-1" />
                        Verified on {review.verifiedSource}
                      </span>
                      {review.reviewUrl && (
                        <a
                          data-testid={`reviews-static-view-link-${review.id}`}
                          href={review.reviewUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs font-semibold text-cyan-700 hover:text-cyan-800 inline-flex items-center"
                        >
                          View on {review.verifiedSource}
                          <ArrowRight className="w-3.5 h-3.5 ml-1" />
                        </a>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Approved Customer Reviews (from API) */}
          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600"></div>
              <p className="mt-4 text-gray-600">Loading more reviews...</p>
            </div>
          ) : reviews.length > 0 ? (
            <div className="mb-16">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 text-center mb-8">
                More From Our Customers
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {reviews.map((review) => (
                  <div
                    key={review.id}
                    className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow duration-300"
                  >
                    {/* Rating */}
                    <div className="flex items-center mb-4">
                      {renderStars(review.rating)}
                    </div>

                    {/* Review Text */}
                    <p className="text-gray-700 mb-6 line-clamp-4">
                      "{review.comment}"
                    </p>

                    {/* Customer Info */}
                    <div className="border-t border-gray-200 pt-4">
                      <p className="font-semibold text-gray-900">
                        {formatCustomerName(review.customerName)}
                      </p>
                      {review.serviceType && (
                        <p className="text-sm text-cyan-600">
                          {review.serviceType}
                        </p>
                      )}
                      {review.created_at && (
                        <p className="text-sm text-gray-500 mt-1">
                          {formatDate(review.created_at)}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          {/* Leave a Review Section */}
          <div
            data-testid="reviews-leave-a-review-section"
            className="bg-white rounded-2xl shadow-lg p-8 md:p-12 mb-16"
          >
            <div className="text-center mb-8">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
                Leave a Review
              </h2>
              <p className="text-gray-600 text-lg">
                Loved your ride? Share your experience to help other travelers.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center max-w-2xl mx-auto">
              <a
                data-testid="reviews-google-review-btn"
                href="https://g.page/r/CRsSkN508n5DECA/review"
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 inline-flex items-center justify-center px-6 py-4 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors duration-300 shadow-md hover:shadow-lg"
              >
                <Star className="w-5 h-5 mr-2 fill-yellow-300 text-yellow-300" />
                Review Us on Google
                <ArrowRight className="w-5 h-5 ml-2" />
              </a>

              <a
                data-testid="reviews-tripadvisor-review-btn"
                href="https://www.tripadvisor.com/Attraction_Review-g147416-d34296506-Reviews-Taxi_98-Nassau_New_Providence_Island_Bahamas.html"
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 inline-flex items-center justify-center px-6 py-4 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg transition-colors duration-300 shadow-md hover:shadow-lg"
              >
                Review Us on Tripadvisor
                <ArrowRight className="w-5 h-5 ml-2" />
              </a>
            </div>
          </div>

          {/* Call to Action */}
          <div className="mt-16 text-center bg-gradient-to-r from-cyan-500 to-blue-600 rounded-2xl p-12 text-white">
            <h2 className="text-3xl font-bold mb-4">
              Have you ridden with us?
            </h2>
            <p className="text-xl mb-8 opacity-90">
              Share your experience and help future customers
            </p>
            <p className="text-lg opacity-90">
              If you recently completed a ride with Taxi-98, you should have received a review link via email or text message.
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default PublicReviews;
